import {orderBy} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {onlyItem} from '../../../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {buildTeammateDisplayName} from '../../../../../../helpers/modelHelpers';
import {InboxModel} from '../../../../../../models/api/inboxModel';
import {InboxesFormatter} from '../../../../../common/formatters/inboxesFormatter';
import {VisualConversationTrackerModel} from './inviteHelpers';

const privateConversationSingleTrackerText = <Trans id="invite-access-onlyyou">Only you can view</Trans>;

/*
 * Props.
 */

export interface InviteDropdownTrackersFooterProps {
  trackers: ReadonlyArray<VisualConversationTrackerModel>;
  conversationInboxes: ReadonlyArray<InboxModel>;
  isTeamConversation: boolean;
}

/*
 * Style.
 */

const StyledFooter = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr;
  grid-auto-flow: column;
  grid-column-gap: 10px;
  align-items: center;

  padding: 12px 14px 12px 14px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledFooterTextDiv = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade80};
  ${ellipsis()};
`;

/*
 * Component.
 */

export const InviteDropdownTrackersFooter: FC<InviteDropdownTrackersFooterProps> = (props) => {
  const theme = useUserTheme();
  const iconName = props.isTeamConversation ? 'sharedFilledSmall' : 'lockSmall';

  const text = buildFooterText(props);

  return (
    <StyledFooter>
      <Icon name={iconName} color={theme.greys.shade70} />
      <StyledFooterTextDiv>{text}</StyledFooterTextDiv>
    </StyledFooter>
  );
};

function buildFooterText(props: InviteDropdownTrackersFooterProps) {
  if (props.isTeamConversation) {
    return buildTeamFooterText(props);
  }
  return buildPrivateFooterText(props);
}

function buildPrivateFooterText(props: InviteDropdownTrackersFooterProps) {
  const {trackers} = props;
  const onlyTracker = onlyItem(trackers);

  if (onlyTracker?.isUser) {
    return privateConversationSingleTrackerText;
  }

  // This happens in practice when looking at another teammate's private conversation through a delegation.
  if (onlyTracker) {
    return (
      <Trans id="invite-footer-private-other">
        Only {buildTeammateDisplayName(onlyTracker.teammate)} can view
      </Trans>
    );
  }

  const otherTrackersCount = trackers.length - 1;
  return (
    <Plural
      id="invite-access-others"
      value={otherTrackersCount}
      one="Only you and 1 other can view"
      other="Only you and # others can view"
    />
  );
}

function buildTeamFooterText(props: InviteDropdownTrackersFooterProps) {
  const {conversationInboxes} = props;
  // Show team channels before any personal channels
  const sortedConversationInboxes = orderBy(conversationInboxes, (inbox) => inbox.namespace, 'desc');

  const formattedInboxes = (
    <InboxesFormatter
      inboxes={sortedConversationInboxes}
      renderInboxName={(name) => <strong>{name}</strong>}
    />
  );
  const localizedSubtitle = (
    <Trans id="invite-footer-teamconversationtext">
      Teammates with access to {formattedInboxes} can view
    </Trans>
  );
  const subtitle = (
    <TooltipCoordinator render={() => localizedSubtitle}>
      <TooltipOverflowCondition>{localizedSubtitle}</TooltipOverflowCondition>
    </TooltipCoordinator>
  );

  return subtitle;
}
